import { useState, useCallback } from "react";
import drawSpaceConfig from "../config/drawSpaceConfig";
import { createEntityId } from "../utils/drawIds";

/*
====================================================================
HOOK D’ÉDITION DES AREAS DU NIVEAU ACTIF
====================================================================

Ce hook gère uniquement :
- les Areas du niveau actif
- la sélection
- le déplacement
- la suppression
- la duplication

Il ne gère pas :
- la création d’ouvrages
- la création de niveaux
*/

export default function useDrawSpaceEditor({
  activeFloorId,
  entities,
  updateEntities,
}) {
  /*
  ================================================================
  ÉTAT LOCAL
  ================================================================
  */

  const [tool, setTool] = useState(drawSpaceConfig.tools.SELECT);
  const [selectedId, setSelectedId] = useState(null);

  const safeEntities = Array.isArray(entities) ? entities : [];

  /*
  ================================================================
  SÉLECTION
  ================================================================
  */

  const selectEntity = useCallback((id) => {
    setSelectedId(id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedId(null);
  }, []);

  /*
  ================================================================
  UPDATE ENTITÉ
  ================================================================
  */

  const replaceEntity = useCallback(
    (updatedEntity) => {
      if (!updatedEntity || !activeFloorId) return;

      const next = safeEntities.map((entity) =>
        entity.id === updatedEntity.id ? updatedEntity : entity
      );

      updateEntities(activeFloorId, next);
    },
    [safeEntities, activeFloorId, updateEntities]
  );

  /*
  ================================================================
  CRÉATION D’AREAS
  ================================================================
  */

  const createRectangle = useCallback(
    ({ x, y, width, height }) => {
      if (!activeFloorId) return;

      const entity = {
        id: createEntityId(),
        type: drawSpaceConfig.entityTypes.AREA_RECT,
        label: "Area",
        floorId: activeFloorId,
        geometry: { x, y, width, height },
        metrics: {},
        attributes: {
          elevation: null,
          height: null,
          areaType: null,
        },
        style: {},
      };

      updateEntities(activeFloorId, [...safeEntities, entity]);
      setSelectedId(entity.id);
    },
    [activeFloorId, safeEntities, updateEntities]
  );

  const createCircle = useCallback(
    ({ cx, cy, r }) => {
      if (!activeFloorId) return;

      const entity = {
        id: createEntityId(),
        type: drawSpaceConfig.entityTypes.CIRCLE,
        label: "Area",
        floorId: activeFloorId,
        geometry: { cx, cy, r },
        metrics: {},
        attributes: {
          elevation: null,
          height: null,
          areaType: null,
        },
        style: {},
      };

      updateEntities(activeFloorId, [...safeEntities, entity]);
      setSelectedId(entity.id);
    },
    [activeFloorId, safeEntities, updateEntities]
  );

  const createLine = useCallback(
    ({ x1, y1, x2, y2 }) => {
      if (!activeFloorId) return;

      const entity = {
        id: createEntityId(),
        type: drawSpaceConfig.entityTypes.LINE,
        label: "Area",
        floorId: activeFloorId,
        geometry: { x1, y1, x2, y2 },
        metrics: {},
        attributes: {
          elevation: null,
          height: null,
          areaType: null,
        },
        style: {},
      };

      updateEntities(activeFloorId, [...safeEntities, entity]);
      setSelectedId(entity.id);
    },
    [activeFloorId, safeEntities, updateEntities]
  );

  const createPolyline = useCallback(
    (points) => {
      if (!activeFloorId || !points || points.length < 3) return;

      const entity = {
        id: createEntityId(),
        type: drawSpaceConfig.entityTypes.AREA_POLYLINE,
        label: "Area",
        floorId: activeFloorId,
        geometry: { points },
        metrics: {},
        attributes: {
          elevation: null,
          height: null,
          areaType: null,
        },
        style: {},
      };

      updateEntities(activeFloorId, [...safeEntities, entity]);
      setSelectedId(entity.id);
    },
    [activeFloorId, safeEntities, updateEntities]
  );

  /*
  ================================================================
  DÉPLACEMENT
  ================================================================
  */

  const moveEntity = useCallback(
    (id, dx, dy) => {
      if (!activeFloorId) return;

      const next = safeEntities.map((entity) => {
        if (entity.id !== id) return entity;

        const geometry = { ...entity.geometry };

        if (geometry.x !== undefined) geometry.x += dx;
        if (geometry.y !== undefined) geometry.y += dy;

        if (geometry.cx !== undefined) geometry.cx += dx;
        if (geometry.cy !== undefined) geometry.cy += dy;

        if (geometry.x1 !== undefined) geometry.x1 += dx;
        if (geometry.y1 !== undefined) geometry.y1 += dy;
        if (geometry.x2 !== undefined) geometry.x2 += dx;
        if (geometry.y2 !== undefined) geometry.y2 += dy;

        if (Array.isArray(geometry.points)) {
          geometry.points = geometry.points.map((point) => ({
            x: point.x + dx,
            y: point.y + dy,
          }));
        }

        return {
          ...entity,
          geometry,
        };
      });

      updateEntities(activeFloorId, next);
    },
    [safeEntities, activeFloorId, updateEntities]
  );

  /*
  ================================================================
  SUPPRESSION
  ================================================================
  */

  const deleteEntity = useCallback(
    (id) => {
      if (!activeFloorId) return;

      const next = safeEntities.filter((entity) => entity.id !== id);
      updateEntities(activeFloorId, next);
      setSelectedId(null);
    },
    [safeEntities, activeFloorId, updateEntities]
  );

  /*
  ================================================================
  DUPLICATION
  ================================================================
  */

  const copyEntity = useCallback(
    (id) => {
      if (!activeFloorId) return;

      const source = safeEntities.find((entity) => entity.id === id);
      if (!source) return;

      const offsetX = drawSpaceConfig.defaults.copyOffsetX;
      const offsetY = drawSpaceConfig.defaults.copyOffsetY;

      const geometry = { ...source.geometry };

      if (geometry.x !== undefined) geometry.x += offsetX;
      if (geometry.y !== undefined) geometry.y += offsetY;

      if (geometry.cx !== undefined) geometry.cx += offsetX;
      if (geometry.cy !== undefined) geometry.cy += offsetY;

      if (geometry.x1 !== undefined) geometry.x1 += offsetX;
      if (geometry.y1 !== undefined) geometry.y1 += offsetY;
      if (geometry.x2 !== undefined) geometry.x2 += offsetX;
      if (geometry.y2 !== undefined) geometry.y2 += offsetY;

      if (Array.isArray(geometry.points)) {
        geometry.points = geometry.points.map((point) => ({
          x: point.x + offsetX,
          y: point.y + offsetY,
        }));
      }

      const clone = {
        ...source,
        id: createEntityId(),
        geometry,
      };

      updateEntities(activeFloorId, [...safeEntities, clone]);
      setSelectedId(clone.id);
    },
    [safeEntities, activeFloorId, updateEntities]
  );

  /*
  ================================================================
  EXPORT
  ================================================================
  */

  return {
    tool,
    setTool,

    selectedId,
    selectEntity,
    clearSelection,

    replaceEntity,
    moveEntity,
    deleteEntity,
    copyEntity,

    createRectangle,
    createCircle,
    createLine,
    createPolyline,
  };
}